/**
 * Loading screen configurations for different screens
 * Use these with the LoadingScreen component
 */

// Loading existing vehicle analysis (fast retrieval)
export const PRODUCT_PAGE_LOADING_CONFIG = {
  title: 'Loading Vehicle Details',
  subtitle: 'Retrieving your analysis',
  completionTitle: 'Ready!',
  completionSubtitle: 'Your vehicle details are loaded',
  completionMessage: 'All information is up to date',
  mainIcon: {
    name: 'car',
    iconSet: 'MaterialCommunityIcons',
  },
  steps: [
    {
      icon: 'download-outline',
      iconSet: 'Ionicons',
      label: 'Retrieving vehicle data',
      duration: 2000,
    },
    {
      icon: 'refresh-outline',
      iconSet: 'Ionicons',
      label: 'Loading your analysis',
      duration: 2000,
    },
    {
      icon: 'checkmark-circle-outline',
      iconSet: 'Ionicons',
      label: 'Finalizing',
      duration: 1000,
    },
  ],
  tip: '🚗 Quick load: Your personalized analysis is already prepared!',
  showProgress: true,
  showTips: true,
};

// Generating new personalized analysis (AI processing)
// Total duration: 25s (5s + 7s + 6s + 7s) — based on prod P90 E2E of ~18s
export const PRODUCT_PAGE_GENERATING_CONFIG = {
  title: 'Preparing Your Vehicle Details',
  subtitle: 'This will take about 20 seconds',
  completionTitle: 'Ready!',
  completionSubtitle: 'Loading complete',
  completionMessage: 'Your vehicle details are ready to view',
  mainIcon: {
    name: 'car',
    iconSet: 'MaterialCommunityIcons',
  },
  steps: [
    {
      icon: 'car',
      iconSet: 'MaterialCommunityIcons',
      label: 'Loading your preferences',
      duration: 5000,
    },
    {
      icon: 'time-outline',
      iconSet: 'Ionicons',
      label: 'Analysing vehicle details',
      duration: 7000,
    },
    {
      icon: 'shield-checkmark-outline',
      iconSet: 'Ionicons',
      label: 'Checking reliability',
      duration: 6000,
    },
    {
      icon: 'bulb-outline',
      iconSet: 'Ionicons',
      label: 'Synthesising essentials',
      duration: 7000,
    },
  ],
  tip: '💡 Did you know? We analyze over 50 data points to give you the most accurate vehicle information.',
  showProgress: true,
  showTips: true,
};

// Legacy export for backward compatibility
export const PRODUCT_PAGE_CONFIG = PRODUCT_PAGE_GENERATING_CONFIG;

// Loading existing recommendations from database
// @deprecated - No longer used. Existing recommendations load instantly without loading screen.
// Kept for backward compatibility only.
export const DASHBOARD_LOADING_CONFIG = {
  title: 'Loading Your Dashboard',
  subtitle: 'Fetching your recommendations',
  completionTitle: 'Welcome Back!',
  completionSubtitle: 'Your dashboard is ready',
  completionMessage: 'All your recommendations are up to date',
  mainIcon: {
    name: 'speedometer',
    iconSet: 'Ionicons',
  },
  steps: [
    {
      icon: 'download-outline',
      iconSet: 'Ionicons',
      label: 'Retrieving your data',
      duration: 2000,
    },
    {
      icon: 'refresh-outline',
      iconSet: 'Ionicons',
      label: 'Updating recommendations',
      duration: 2000,
    },
    {
      icon: 'checkmark-circle-outline',
      iconSet: 'Ionicons',
      label: 'Finalizing',
      duration: 1000,
    },
  ],
  tip: '🚗 Pro tip: Check your recommendations daily to catch new vehicles that match your preferences!',
  showProgress: true,
  showTips: true,
};

// Generating new recommendations with AI
// Total duration: 120s (20s + 30s + 35s + 35s)
export const DASHBOARD_GENERATING_CONFIG = {
  title: 'Tailoring Your Recommendations',
  subtitle: 'Scanning every listing to find what fits you',
  completionTitle: 'Recommendations Ready!',
  completionSubtitle: 'Your tailored recommendations are ready',
  completionMessage: 'We found the best listings for your needs',
  mainIcon: {
    name: 'sparkles',
    iconSet: 'Ionicons',
  },
  steps: [
    {
      icon: 'person-outline',
      iconSet: 'Ionicons',
      label: 'Analyzing your preferences',
      duration: 20000,
    },
    {
      icon: 'search-outline',
      iconSet: 'Ionicons',
      label: 'Searching vehicle database',
      duration: 30000,
    },
    {
      icon: 'analytics-outline',
      iconSet: 'Ionicons',
      label: 'Calculating fit scores',
      duration: 35000,
    },
    {
      icon: 'sparkles-outline',
      iconSet: 'Ionicons',
      label: 'Tailoring recommendations',
      duration: 35000,
    },
  ],
  tip: '✨ Did you know? Our AI analyzes thousands of vehicles to find your perfect match based on your unique preferences!',
  showProgress: true,
  showTips: true,
};

// Legacy config - kept for backward compatibility
export const DASHBOARD_CONFIG = DASHBOARD_LOADING_CONFIG;

/**
 * Get loading config by screen type
 * @param {string} screenType - 'product' or 'dashboard'
 * @param {string} loadingType - 'loading' or 'generating' (optional)
 * @returns {object} Loading screen configuration
 * @note For dashboard with loadingType='loading', parent should skip loading screen entirely
 */
export const getLoadingConfig = (screenType, loadingType = 'loading') => {
  switch (screenType) {
    case 'product':
      return loadingType === 'generating'
        ? PRODUCT_PAGE_GENERATING_CONFIG
        : PRODUCT_PAGE_LOADING_CONFIG;
    case 'dashboard':
      // For existing recommendations, parent should skip loading screen entirely
      return loadingType === 'generating' ? DASHBOARD_GENERATING_CONFIG : null;
    default:
      return PRODUCT_PAGE_LOADING_CONFIG;
  }
};
